// ----------------------------------------------------------------------

/** 路由权限规则（对齐 nest-api menu-seed.constant.ts） */
export type RoutePermissionRule = {
  /** 路由模式，支持 :param 动态段 */
  pattern: string;
  /** 所需权限码（resource:action） */
  permissionCode: string;
};

export type NavScope = 'dashboard' | 'hq';

/** 无需 ACL 校验的路由（精确匹配，多为 index 重定向） */
export const ROUTE_PUBLIC_PATHS: Record<NavScope, readonly string[]> = {
  dashboard: ['/dashboard', '/dashboard/business', '/dashboard/members', '/dashboard/system'],
  /** HQ 前缀/首页仅在 HqGuard 确认总部租户（code=0000）后放行，此处跳过页面级 ACL */
  hq: [
    '/hq',
    '/hq/overview',
    '/hq/cities',
    '/hq/tenant',
    '/hq/app-user',
    '/hq/finance',
    '/hq/order',
    '/hq/account',
    '/hq/system',
  ],
};

/**
 * Dashboard 路由权限（越具体的路由越靠前）
 * 来源：DASHBOARD_MENU_SEED_GROUPS
 */
export const DASHBOARD_ROUTE_PERMISSIONS: RoutePermissionRule[] = [
  // 订单
  { pattern: '/dashboard/business/overdue', permissionCode: 'order:list' },
  { pattern: '/dashboard/business/orders/rent', permissionCode: 'order:list' },
  { pattern: '/dashboard/business/orders/rent-to-buy', permissionCode: 'order:list' },
  { pattern: '/dashboard/business/orders/installment', permissionCode: 'order:list' },
  { pattern: '/dashboard/business/orders/rental', permissionCode: 'order:list' },
  { pattern: '/dashboard/business/orders', permissionCode: 'order:list' },
  // 商品
  { pattern: '/dashboard/business/products/edit/:id', permissionCode: 'product:manage' },
  { pattern: '/dashboard/business/products/create', permissionCode: 'product:manage' },
  { pattern: '/dashboard/business/products/list', permissionCode: 'product:list' },
  { pattern: '/dashboard/business/products/category', permissionCode: 'product:list' },
  { pattern: '/dashboard/business/products', permissionCode: 'product:list' },
  // 资产
  { pattern: '/dashboard/business/assets/edit/:id', permissionCode: 'asset:update' },
  { pattern: '/dashboard/business/assets/list', permissionCode: 'asset:list' },
  { pattern: '/dashboard/business/assets/transfers', permissionCode: 'asset:list' },
  { pattern: '/dashboard/business/assets/deleted-records', permissionCode: 'asset:list' },
  { pattern: '/dashboard/business/assets/category', permissionCode: 'asset:list' },
  { pattern: '/dashboard/business/assets', permissionCode: 'asset:list' },
  // 门店
  { pattern: '/dashboard/business/stores', permissionCode: 'store:list' },
  // 会员
  { pattern: '/dashboard/members/list', permissionCode: 'member:list' },
  // 财务
  { pattern: '/dashboard/finance/overview', permissionCode: 'payment:list' },
  { pattern: '/dashboard/finance/bills', permissionCode: 'payment:list' },
  { pattern: '/dashboard/finance/deposits', permissionCode: 'payment:list' },
  { pattern: '/dashboard/finance/payments', permissionCode: 'payment:list' },
  { pattern: '/dashboard/finance/refunds', permissionCode: 'refund:list' },
  { pattern: '/dashboard/finance/settlements', permissionCode: 'settlement:list' },
  { pattern: '/dashboard/finance/withdrawals', permissionCode: 'settlement:list' },
  { pattern: '/dashboard/finance/clearing-records', permissionCode: 'settlement:list' },
  { pattern: '/dashboard/finance', permissionCode: 'payment:list' },
  // 组织
  { pattern: '/dashboard/organization/users', permissionCode: 'staff:list' },
  { pattern: '/dashboard/organization/roles', permissionCode: 'role:list' },
  { pattern: '/dashboard/organization', permissionCode: 'staff:list' },
  // 系统
  { pattern: '/dashboard/system/settings', permissionCode: 'tenant_profile:read' },
];

/**
 * HQ 路由权限（越具体的路由越靠前）
 * 来源：HQ_MENU_SEED_GROUPS
 */
export const HQ_ROUTE_PERMISSIONS: RoutePermissionRule[] = [
  // 租户
  { pattern: '/hq/tenant/list', permissionCode: 'tenant:list' },
  { pattern: '/hq/tenant/enterprise-verification', permissionCode: 'tenant:list' },
  // 用户
  { pattern: '/hq/app-user/list', permissionCode: 'user:list' },
  { pattern: '/hq/app-user/deletions', permissionCode: 'user:list' },
  { pattern: '/hq/app-user/:id', permissionCode: 'user:list' },
  // 财务
  { pattern: '/hq/finance/overview', permissionCode: 'payment:list' },
  { pattern: '/hq/finance/payments', permissionCode: 'payment:list' },
  { pattern: '/hq/finance/clearing-records', permissionCode: 'settlement:manage' },
  { pattern: '/hq/finance/settlements', permissionCode: 'settlement:manage' },
  { pattern: '/hq/finance/withdrawals', permissionCode: 'settlement:manage' },
  { pattern: '/hq/finance/clearing-rules', permissionCode: 'settlement:manage' },
  { pattern: '/hq/finance/reconciliation', permissionCode: 'settlement:manage' },
  { pattern: '/hq/finance/refunds', permissionCode: 'refund:audit' },
  { pattern: '/hq/finance/participants', permissionCode: 'settlement:manage' },
  // 订单
  { pattern: '/hq/order/overview', permissionCode: 'order:list' },
  { pattern: '/hq/order/overdue', permissionCode: 'order:list' },
  { pattern: '/hq/order/list', permissionCode: 'order:list' },
  // 账号
  { pattern: '/hq/account/admin-users', permissionCode: 'staff:manage' },
  { pattern: '/hq/account/roles', permissionCode: 'role:manage' },
  { pattern: '/hq/account/permissions', permissionCode: 'permission:manage' },
  // 系统
  { pattern: '/hq/system/menus', permissionCode: 'menu:manage' },
  { pattern: '/hq/system/settings', permissionCode: 'platform_setting:manage' },
  { pattern: '/hq/system/audit-logs', permissionCode: 'audit_log:list' },
  { pattern: '/hq/system/monitoring', permissionCode: 'system_config:manage' },
  { pattern: '/hq/system/configurations', permissionCode: 'system_config:manage' },
  { pattern: '/hq/system/overview', permissionCode: 'system_config:manage' },
];

/** 按作用域获取路由权限规则 */
export function getRoutePermissionRules(scope: NavScope): RoutePermissionRule[] {
  return scope === 'dashboard' ? DASHBOARD_ROUTE_PERMISSIONS : HQ_ROUTE_PERMISSIONS;
}
